import { Link, Redirect, router } from 'expo-router';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  CURRENT_LEGAL_EFFECTIVE_DATE,
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
} from '@/src/features/legal/legal-documents';
import { hasRecordedLegalAcceptance } from '@/src/features/legal/legal-service';
import { useAcceptCurrentLegal } from '@/src/features/legal/use-legal';
import { useProfile } from '@/src/features/profile/use-profile';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

export default function LegalConsentScreen() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const profileQuery = useProfile(user?.uid);
  const refetchProfile = profileQuery.refetch;
  const acceptLegalMutation = useAcceptCurrentLegal(user?.uid);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      return undefined;
    }

    void refetchProfile();

    const interval = setInterval(() => {
      void refetchProfile();
    }, 15_000);

    return () => clearInterval(interval);
  }, [refetchProfile, user?.uid]);

  if (profileQuery.isLoading) {
    return <LoadingView message="Checking account agreements..." />;
  }

  if (profileQuery.isError) {
    return (
      <Screen title="Terms and Privacy">
        <ErrorView
          message={profileQuery.error.message}
          onRetry={() => {
            void profileQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  if (profileQuery.isFetching && !profileQuery.isFetchedAfterMount) {
    return <LoadingView message="Checking account agreements..." />;
  }

  if (hasRecordedLegalAcceptance(profileQuery.data?.legal)) {
    return <Redirect href="/home" />;
  }

  async function handleAccept() {
    try {
      const latestProfile = await profileQuery.refetch();

      if (hasRecordedLegalAcceptance(latestProfile.data?.legal)) {
        router.replace('/home');
        return;
      }

      await acceptLegalMutation.mutateAsync({
        email: user?.email ?? null,
        source: 'update',
      });
      showSuccess(
        'Your Terms of Service and Privacy Policy acceptance was recorded.',
        'Agreements accepted',
      );
      router.replace('/home');
    } catch (caughtError) {
      const latestProfile = await profileQuery.refetch();

      if (hasRecordedLegalAcceptance(latestProfile.data?.legal)) {
        router.replace('/home');
        return;
      }

      showError(
        caughtError,
        'Unable to save your acceptance. Please try again.',
      );
    }
  }

  return (
    <Screen
      title="Terms and Privacy"
      subtitle="Review and accept the current Frienzzi agreements to continue."
    >
      <View style={styles.panel}>
        <Text style={styles.heading}>Current versions</Text>
        <Text style={styles.copy}>
          Effective {CURRENT_LEGAL_EFFECTIVE_DATE}. Terms version{' '}
          {CURRENT_TERMS_VERSION}. Privacy Policy version{' '}
          {CURRENT_PRIVACY_VERSION}.
        </Text>
        <Text style={styles.copy}>
          We keep a timestamped record of each version you accept.
        </Text>
        <View style={styles.links}>
          <Link href="/terms" style={styles.link}>
            Read Terms of Service
          </Link>
          <Link href="/privacy" style={styles.link}>
            Read Privacy Policy
          </Link>
        </View>
      </View>

      <View style={styles.legalField}>
        <Pressable
          accessibilityRole="checkbox"
          accessibilityState={{ checked: accepted }}
          onPress={() => setAccepted((value) => !value)}
          style={styles.checkboxRow}
        >
          <View style={[styles.checkbox, accepted ? styles.checked : null]}>
            {accepted ? <Text style={styles.checkmark}>✓</Text> : null}
          </View>
          <Text style={styles.legalText}>
            I have read and agree to the current Terms of Service and Privacy
            Policy.
          </Text>
        </Pressable>
      </View>

      <Button
        title="Accept and continue"
        disabled={!accepted}
        loading={acceptLegalMutation.isPending}
        onPress={handleAccept}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  heading: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '800',
  },
  copy: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
  links: {
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  link: {
    color: colors.primary,
    fontSize: 15,
    fontWeight: '800',
  },
  legalField: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    padding: spacing.lg,
  },
  checkboxRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  checkbox: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
    borderRadius: 6,
    borderWidth: 2,
    height: 24,
    justifyContent: 'center',
    marginTop: 1,
    width: 24,
  },
  checked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkmark: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 18,
  },
  legalText: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    lineHeight: 22,
  },
});
